import { useAppDispatch, useAppSelector } from "../../app";
import type { AppDispatch } from "../../app/store";
import { DefaultOptionType } from "antd/es/select";
import { useCallback, useEffect, useMemo, useState } from "react";
import { OptionType } from "../interfaces/classifier";
import {
  fetchClassifierOptions,
  fetchClassifierOptionsSearch,
} from "../utils/classifier";
import {
  changeActiveButton,
  changeActiveEntity,
  changeAnnotating,
  changeEditing,
  changeHasBeenEdited,
} from "../../app/store/appSlice";
import {
  CategorizationAnnotation,
  CategorizationSetType,
  CategorizationWithConfig,
} from "../../configMap/interfaces/category";
import {
  removeCategorizationAnnotation,
  removeIncompleteCategorization,
  updateCategorizationAnnotation,
} from "../store/metadataSlice";
import useEntityAnnotations from "./useEntityAnnotations";
import { buildSearchQuery } from "../../common/utilities/requests";
import { Status } from "../../common/status/status";
import { useTranslation } from "react-i18next";
import useParentCategorization from "./useParentCategorization";
import { debounce } from "lodash";
import { findOriginalIndex } from "../../documentTypes/utils/synchronizeAnnotationHelper";
import useCategorizationEvents from "./useCategorizationEvents";
import useParentEntity from "./useParentEntity";
import { meetsMinimumSearchLength } from "../utils/categorizationSearch";
import selectCategorizationAnnotationId from "../selectors/selectCategorizationAnnotationId";
import { notification } from "antd";
import { calculateHideDelay } from "../../common/utilities/text";

const useDynamicCategorization = (
  categorizationWithConfig: CategorizationWithConfig,
  index: number,
  groupBlockId?: string,
) => {
  const {
    i18n: { language },
  } = useTranslation();

  const { required, categorization } = categorizationWithConfig;
  const { id, search, setType } = categorization;

  const dispatch = useAppDispatch();

  const document = useAppSelector(
    (state) => state.documentReducer.activeDocument,
  );
  const activeDocumentSet = useAppSelector(
    (state) => state.documentSetsReducer.activeDocumentSet,
  );
  const annotations = useAppSelector(
    (state) => state.annotationReducer.tableControlAnnotations,
  );

  const categorizationAnnotation = useAppSelector((state) =>
    selectCategorizationAnnotationId(state, {
      documentId: document?.id,
      categorizationId: id,
      groupBlockId: groupBlockId,
      index: index,
    }),
  );

  const {
    derivedCategorizationOptions,
    loadingCategorizationIds,
    incompleteCategorizations,
  } = useAppSelector((state) => state.metadataReducer);

  const normalizeEntityAnnotations = useEntityAnnotations();

  const {
    isCategorizationDisabledByParent,
    disabledParentTooltip,
    getAttributeFilter,
  } = useParentCategorization(
    categorizationAnnotation,
    categorization,
    document?.id,
    groupBlockId,
    index,
    activeDocumentSet?.input?.attribute,
  );

  const { attributeFilter: entityParentAttributeFilter } =
    useParentEntity(categorization);

  const [options, setOptions] = useState<Array<OptionType>>([]);
  const [emptySearchResult, setEmptySearchResult] = useState(false);
  const [loading, setLoading] = useState(false);

  const isCategorizationLoading = useMemo(() => {
    return loadingCategorizationIds.includes(categorization.id);
  }, [loadingCategorizationIds, categorization.id]);

  const categorizationOptions = useMemo(() => {
    if (categorization.setType === CategorizationSetType.DERIVED) {
      return derivedCategorizationOptions[categorization.id] ?? [];
    }
    return options;
  }, [derivedCategorizationOptions, categorization, options]);

  const tabId = useMemo(() => {
    if (groupBlockId) {
      return `categorization-${id}-${groupBlockId}-${index}`;
    }

    return `categorization-${id}-${index}`;
  }, [id, index, groupBlockId]);

  const isLoadingValue = useMemo(() => {
    return categorizationAnnotation?.isLoading ?? false;
  }, [categorizationAnnotation]);

  const selectedCategory = useMemo(() => {
    if (!categorizationAnnotation) {
      return null;
    }

    if (isLoadingValue) {
      return null;
    }

    // FIXED and DYNAMIC are using the same structure now
    return {
      value: categorizationAnnotation.value,
      visibleValue: categorizationAnnotation.visibleValue,
    };
  }, [categorizationAnnotation, isLoadingValue]);

  const { callEvents } = useCategorizationEvents();

  const removeIncompleteCategorizationInRedux = useCallback(
    (categorizationId: string, index: number, groupBlockId?: string) => {
      dispatch(
        removeIncompleteCategorization({
          categorizationId,
          groupBlockId,
          index,
        }),
      );
    },
    [dispatch],
  );

  const updateCategorizationAnnotationAsync =
    (payload: CategorizationAnnotation) => (dispatch: AppDispatch) => {
      return new Promise<void>((resolve) => {
        dispatch(updateCategorizationAnnotation(payload));
        resolve();
      });
    };

  const handleChange = useCallback(
    async (key: string, callback: () => void) => {
      if (!document?.id) {
        return;
      }

      const found = categorizationOptions.find((o) => o.key === key);

      dispatch(changeHasBeenEdited(true));
      if (found) {
        const originalIndex = findOriginalIndex(
          annotations,
          [],
          groupBlockId,
          index,
        );

        await dispatch(
          updateCategorizationAnnotationAsync({
            documentId: document.id,
            categorizationId: id,
            externalDataId: found.key,
            groupBlockId,
            index,
            originalIndex,
            isByUser: true,
            visibleValue: found.visibleValue,
            value: found.value,
          }),
        );

        removeIncompleteCategorizationInRedux(id, index, groupBlockId);

        // Re-normalization is currently only relevant for fixed categorizations, not dynamic ones.
        normalizeEntityAnnotations(
          id,
          found.value,
          index,
          !!groupBlockId,
        ).catch(() => undefined);

        callback();
        return;
      }

      dispatch(
        removeCategorizationAnnotation({
          documentId: document.id,
          categorizationId: id,
          groupBlockId,
          index,
        }),
      );
      callback();
    },
    [
      document?.id,
      id,
      groupBlockId,
      index,
      dispatch,
      normalizeEntityAnnotations,
      removeIncompleteCategorizationInRedux,
      annotations,
      categorizationOptions,
    ],
  );

  const handleFocus = useCallback(() => {
    dispatch(changeEditing(tabId));
    dispatch(changeActiveEntity(undefined));
    dispatch(changeAnnotating(false));
    dispatch(changeActiveButton({ id: tabId }));
  }, [dispatch, tabId]);

  const handleBlur = useCallback(() => {
    dispatch(changeEditing(null));
    dispatch(changeActiveButton(undefined));
  }, [dispatch]);

  const handleHttpRequest = useCallback(
    async (signal: AbortSignal) => {
      if (!search?.endpoint) {
        return;
      }

      try {
        setLoading(true);
        const { data } = await fetchClassifierOptions(id, signal);

        // Store prefetched options
        setOptions(data);

        if (data.length === 0) {
          setEmptySearchResult(true);
        }
      } catch (e) {
        if (signal.aborted) {
          return;
        }

        console.error("Prefetch request failed:", e);
      } finally {
        setLoading(false);
      }
    },
    [id, search],
  );

  const handleSearch = useCallback(
    // Deps are deliberately narrower than the compiler can infer: it widens
    // `activeDocumentSet?.input` to the whole `activeDocumentSet` object.
    // Widening would re-create this callback (and the debounced wrapper built
    // from it) whenever that Redux object is re-created with unchanged
    // contents, causing redundant requests. The narrow dep is the correct one.
    // eslint-disable-next-line react-hooks/preserve-manual-memoization -- narrow optional-chain dep is intentional; widening causes redundant fetches
    async (value: string) => {
      if (!search) {
        return;
      }

      if (search.prefetch) {
        return;
      }

      const meetsSearchLength = meetsMinimumSearchLength(
        value,
        search.minSearchLength,
        search.parentCategorization?.id,
        categorization.id,
        categorizationAnnotation?.value,
      );
      if (!meetsSearchLength) {
        return;
      }

      try {
        setLoading(true);
        const input_ids = activeDocumentSet?.input
          ? [activeDocumentSet.input.id]
          : undefined;
        const attributeFilter = getAttributeFilter(value);

        const searchQuery = buildSearchQuery(
          search,
          value,
          null,
          language,
          input_ids,
          attributeFilter || entityParentAttributeFilter,
        );
        const { data } = await fetchClassifierOptionsSearch(
          search.endpoint,
          searchQuery,
        );
        setOptions(data);

        if (data.length === 0) {
          setEmptySearchResult(true);
        }
      } catch (error: unknown) {
        console.error("Failed to search categorization data:", error);
        const errorMessage =
          "Something went wrong when searching for categorization data.";
        notification.error({
          title: "Failed Search",
          description: errorMessage,
          duration: calculateHideDelay(errorMessage),
        });
        setEmptySearchResult(true);
      } finally {
        setLoading(false);
      }
    },
    [
      search,
      categorization,
      language,
      getAttributeFilter,
      entityParentAttributeFilter,
      activeDocumentSet?.input,
      categorizationAnnotation,
    ],
  );

  const debouncedHandleSearch = useMemo(
    () => debounce(handleSearch, 500),
    [handleSearch],
  );

  const handleFilter = useCallback(
    (input: string, option?: DefaultOptionType) => {
      if (setType === CategorizationSetType.DYNAMIC) {
        return true;
      }

      // antd types `option` as optional and its `label` as the wider
      // `ReactNode`. The Select always renders its options from `OptionType`,
      // whose `label` is a string.
      const label = option!.label as string;
      return label.toLowerCase().includes(input.toLowerCase());
    },
    [setType],
  );

  const status = useMemo((): "" | "warning" | "error" => {
    if (
      activeDocumentSet?.status === Status.ExportFailed &&
      !selectedCategory?.value &&
      required
    ) {
      return "error";
    }
    return "";
  }, [activeDocumentSet?.status, selectedCategory, required]);

  useEffect(() => {
    if (setType === CategorizationSetType.DERIVED) {
      return;
    }

    if (!search) {
      return;
    }

    if (setType === CategorizationSetType.DYNAMIC && search.prefetch) {
      const controller = new AbortController();
      // handleHttpRequest is async and only sets state after awaiting the
      // request; the rule cannot see through the async boundary.
      // eslint-disable-next-line react-hooks/set-state-in-effect -- setState happens after an await, not synchronously in the effect body
      handleHttpRequest(controller.signal);

      return () => {
        controller.abort();
      };
    }
  }, [handleHttpRequest, search, setType, id]);

  useEffect(() => {
    return () => {
      debouncedHandleSearch.cancel();
    };
  }, [debouncedHandleSearch]);

  const isIncomplete = useMemo(() => {
    const found = incompleteCategorizations.find((bc) => {
      return (
        bc.categorizationId === categorization.id &&
        bc.index === index &&
        bc.groupBlockId === groupBlockId
      );
    });

    return !!found;
  }, [incompleteCategorizations, categorization, index, groupBlockId]);

  return {
    tabId,
    selectedCategory,
    categorizationOptions,
    handleChange,
    handleFocus,
    handleBlur,
    handleSearch: debouncedHandleSearch,
    handleFilter,
    status,
    isCategorizationDisabledByParent,
    disabledParentTooltip,
    isIncomplete,
    callEvents,
    isCategorizationLoading,
    emptySearchResult,
    loading,
    isLoadingValue,
  };
};

export default useDynamicCategorization;
